import React, { FormEvent, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowRightLeft,
  CheckCircle2,
  ChevronDown,
  KeyRound,
  Link2,
  Lock,
  RefreshCw,
  Unplug
} from 'lucide-react';
import TradingTerminalLayout from './TradingTerminalLayout';
import TradingPairHeader from './TradingPairHeader';
import OrderBookWithTrades from './OrderBookWithTrades';
import ChartToolbar from './ChartToolbar';
import TradingChart from './TradingChart';
import PositionsPanel from './PositionsPanel';
import { useUnifiedPrices } from '../contexts/UnifiedPriceContext';
import { binanceExchangeApi, BinanceAccountOverview, BinanceConnection } from '../services/binanceExchangeApi';

type ExchangeId = 'binance' | 'bybit' | 'bitget' | 'okx' | 'coinbase';
type MarketType = 'spot' | 'futures';
type OrderSide = 'buy' | 'sell';
type OrderType = 'market' | 'limit';
type WalletType = 'spot' | 'futures';
type BalanceKind = 'available' | 'locked' | 'earn' | 'wallet';

interface ExchangeDefinition {
  id: ExchangeId;
  name: string;
  marketTypes: MarketType[];
  requiresPassphrase?: boolean;
  usesCoinbaseJwt?: boolean;
}

interface SavedConnection {
  id?: string;
  exchange: ExchangeId;
  label: string;
  maskedKey: string;
  connectedAt: string;
  isTestnet?: boolean;
}

interface ExchangesTradingPageProps {
  selectedPair: string;
  onPairChange: (pair: string) => void;
}

interface ExchangeWalletBalance {
  walletType: WalletType;
  asset: string;
  displayAsset: string;
  balanceKind: BalanceKind;
  total: number;
  available: number;
  locked: number;
}

const EXCHANGES: ExchangeDefinition[] = [
  { id: 'binance', name: 'Binance', marketTypes: ['spot', 'futures'] },
  { id: 'bybit', name: 'Bybit', marketTypes: ['spot', 'futures'] },
  { id: 'bitget', name: 'Bitget', marketTypes: ['futures'], requiresPassphrase: true },
  { id: 'okx', name: 'OKX', marketTypes: ['spot', 'futures'], requiresPassphrase: true },
  { id: 'coinbase', name: 'Coinbase', marketTypes: ['spot'], usesCoinbaseJwt: true }
];

const maskApiKey = (value: string) => {
  const trimmed = value.trim();
  if (trimmed.length <= 8) return 'saved key';
  return `${trimmed.slice(0, 4)}...${trimmed.slice(-4)}`;
};

const getErrorMessage = (error: unknown, fallback: string) => (
  error instanceof Error ? error.message : fallback
);

const formatPairForCoinbase = (symbol: string) => {
  if (symbol.endsWith('USDT')) {
    return `${symbol.replace('USDT', '')}-USD`;
  }
  return symbol.replace('/', '-');
};

const formatBalanceAmount = (value: number) => {
  if (!Number.isFinite(value)) return '--';
  if (value >= 1000) return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
  if (value >= 1) return value.toLocaleString('en-US', { maximumFractionDigits: 6 });
  return value.toLocaleString('en-US', { maximumFractionDigits: 8 });
};

const normalizeBinanceAsset = (asset: string) => {
  if (asset.startsWith('LD') && asset.length > 2) {
    return {
      displayAsset: asset.slice(2),
      balanceKind: 'earn' as const
    };
  }

  return {
    displayAsset: asset,
    balanceKind: 'wallet' as const
  };
};

const balancesFromOverview = (overview: BinanceAccountOverview): ExchangeWalletBalance[] => {
  const spotBalances = (overview.spotAccount?.balances || [])
    .map(balance => {
      const available = Number(balance.free) || 0;
      const locked = Number(balance.locked) || 0;
      const normalized = normalizeBinanceAsset(balance.asset);
      return {
        walletType: 'spot' as const,
        asset: balance.asset,
        displayAsset: normalized.displayAsset,
        balanceKind: normalized.balanceKind,
        total: available + locked,
        available,
        locked
      };
    });

  const futuresBalances = (overview.futuresBalances || [])
    .map(balance => {
      const total = Number(balance.balance) || 0;
      const available = Number(balance.availableBalance) || 0;
      return {
        walletType: 'futures' as const,
        asset: balance.asset,
        displayAsset: balance.asset,
        balanceKind: 'wallet' as const,
        total,
        available,
        locked: Math.max(total - available, 0)
      };
    });

  return [...spotBalances, ...futuresBalances]
    .filter(balance => balance.total > 0 || balance.available > 0 || balance.locked > 0)
    .sort((a, b) => {
      if (a.displayAsset === 'USDT') return -1;
      if (b.displayAsset === 'USDT') return 1;
      return b.total - a.total;
    });
};

const ExchangeConnectionPanel: React.FC<{
  selectedExchange: ExchangeId;
  onExchangeChange: (exchange: ExchangeId) => void;
  connection?: SavedConnection;
  onConnect: (params: {
    exchange: ExchangeId;
    label: string;
    apiKey: string;
    apiSecret: string;
    passphrase: string;
    coinbaseKeyName: string;
    coinbasePrivateKey: string;
    isTestnet: boolean;
  }) => Promise<void>;
  onDisconnect: () => Promise<void>;
}> = ({ selectedExchange, onExchangeChange, connection, onConnect, onDisconnect }) => {
  const exchange = EXCHANGES.find(item => item.id === selectedExchange) || EXCHANGES[0];
  const [label, setLabel] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [passphrase, setPassphrase] = useState('');
  const [coinbaseKeyName, setCoinbaseKeyName] = useState('');
  const [coinbasePrivateKey, setCoinbasePrivateKey] = useState('');
  const [isTestnet, setIsTestnet] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formMessage, setFormMessage] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    setFormMessage(null);

    try {
      await onConnect({
        exchange: selectedExchange,
        label: label.trim() || exchange.name,
        apiKey,
        apiSecret,
        passphrase,
        coinbaseKeyName,
        coinbasePrivateKey,
        isTestnet
      });

      setApiKey('');
      setApiSecret('');
      setPassphrase('');
      setCoinbaseKeyName('');
      setCoinbasePrivateKey('');
    } catch (error: unknown) {
      setFormMessage(getErrorMessage(error, 'Unable to connect exchange account.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="border-b border-slate-200 p-4 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-slate-900">Exchange Connection</div>
          <div className="text-xs text-slate-500">Valore routes orders through the selected exchange account.</div>
        </div>
        {connection ? (
          <span className="inline-flex items-center gap-1.5 rounded bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700">
            <CheckCircle2 size={13} />
            Connected
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
            <Unplug size={13} />
            Not connected
          </span>
        )}
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-slate-500">Exchange</label>
        <div className="relative">
          <select
            value={selectedExchange}
            onChange={(event) => onExchangeChange(event.target.value as ExchangeId)}
            className="custom-select w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-medium text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
          >
            {EXCHANGES.map(item => (
              <option key={item.id} value={item.id}>{item.name}</option>
            ))}
          </select>
          <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
        </div>
      </div>

      {connection ? (
        <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm font-semibold text-slate-900">{connection.label}</div>
              <div className="mt-1 text-xs text-slate-500">{exchange.name} account connected</div>
            </div>
            <button
              type="button"
              onClick={onDisconnect}
              disabled={isSubmitting}
              className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:border-red-200 hover:text-red-600"
            >
              Disconnect
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Connection label</label>
            <input
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              placeholder={`${exchange.name} main account`}
            />
          </div>

          {exchange.usesCoinbaseJwt ? (
            <>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-500">Coinbase key name</label>
                <input
                  value={coinbaseKeyName}
                  onChange={(event) => setCoinbaseKeyName(event.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                  placeholder="organizations/.../apiKeys/..."
                  required
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-500">ECDSA private key</label>
                <textarea
                  value={coinbasePrivateKey}
                  onChange={(event) => setCoinbasePrivateKey(event.target.value)}
                  className="min-h-[96px] w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                  placeholder="-----BEGIN EC PRIVATE KEY-----"
                  required
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-500">API key</label>
                <input
                  value={apiKey}
                  onChange={(event) => setApiKey(event.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-500">API secret</label>
                <input
                  type="password"
                  value={apiSecret}
                  onChange={(event) => setApiSecret(event.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              {exchange.requiresPassphrase && (
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-500">Passphrase</label>
                  <input
                    type="password"
                    value={passphrase}
                    onChange={(event) => setPassphrase(event.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              )}
            </>
          )}

          <label className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
            <span className="text-sm font-medium text-slate-700">Testnet / demo</span>
            <input
              type="checkbox"
              checked={isTestnet}
              onChange={(event) => setIsTestnet(event.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
          </label>

          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            <div className="flex gap-2">
              <AlertTriangle size={15} className="mt-0.5 flex-shrink-0" />
            <span>Create exchange keys with read and trade permissions only. Keep withdrawals disabled.</span>
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
          >
            <Link2 size={16} />
            {isSubmitting ? 'Connecting...' : `Connect ${exchange.name}`}
          </button>

          {formMessage && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
              {formMessage}
            </div>
          )}
        </form>
      )}
    </div>
  );
};

const ExchangeAccountPanel: React.FC<{
  selectedExchange: ExchangeId;
  connection?: SavedConnection;
  balances: ExchangeWalletBalance[];
  overview?: BinanceAccountOverview | null;
  isLoading: boolean;
  onRefresh: () => Promise<void>;
}> = ({ selectedExchange, connection, balances, overview, isLoading, onRefresh }) => {
  const [activeWallet, setActiveWallet] = useState<WalletType>('spot');
  const [transferDirection, setTransferDirection] = useState<'MAIN_UMFUTURE' | 'UMFUTURE_MAIN'>('MAIN_UMFUTURE');
  const [transferAsset, setTransferAsset] = useState('USDT');
  const [transferAmount, setTransferAmount] = useState('');
  const [convertAsset, setConvertAsset] = useState('');
  const [convertAmount, setConvertAmount] = useState('');
  const [isTransferring, setIsTransferring] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const visibleBalances = balances.filter(balance => balance.walletType === activeWallet);
  const spotUsdt = balances.find(balance => balance.walletType === 'spot' && balance.asset === 'USDT');
  const futuresUsdt = balances.find(balance => balance.walletType === 'futures' && balance.asset === 'USDT');
  const spotBalances = balances.filter(balance => balance.walletType === 'spot');
  const futuresBalances = balances.filter(balance => balance.walletType === 'futures');
  const spotBtcTotal = spotBalances
    .filter(balance => balance.displayAsset === 'BTC')
    .reduce((sum, balance) => sum + balance.total, 0);
  const futuresWalletTotal = futuresBalances.reduce((sum, balance) => sum + balance.total, 0);
  const transferableAssets = Array.from(new Set(
    balances
      .filter(balance => balance.balanceKind === 'wallet' && balance.available > 0)
      .map(balance => balance.asset)
  )).sort((a, b) => {
    if (a === 'USDT') return -1;
    if (b === 'USDT') return 1;
    return a.localeCompare(b);
  });
  const convertibleBalances = spotBalances.filter(balance => (
    balance.balanceKind === 'wallet' &&
    balance.asset !== 'USDT' &&
    balance.available > 0
  ));
  const selectedConvertBalance = convertibleBalances.find(balance => balance.asset === convertAsset) || convertibleBalances[0];
  const uniqueErrors = Array.from(new Set(Object.values(overview?.errors || {}).filter(Boolean)));

  React.useEffect(() => {
    if (!convertAsset && convertibleBalances.length > 0) {
      setConvertAsset(convertibleBalances[0].asset);
    }
  }, [convertAsset, convertibleBalances]);

  const handleTransfer = async (event: FormEvent) => {
    event.preventDefault();
    setMessage(null);

    if (!connection?.id || selectedExchange !== 'binance') {
      setMessage('Connect Binance before transferring between exchange wallets.');
      return;
    }

    if (connection.isTestnet) {
      setMessage('Binance universal transfer is only available on live Binance accounts.');
      return;
    }

    if (!transferAmount || Number(transferAmount) <= 0) {
      setMessage('Enter a valid transfer amount.');
      return;
    }

    setIsTransferring(true);

    try {
      await binanceExchangeApi.transfer({
        connectionId: connection.id,
        transferType: transferDirection,
        asset: transferAsset,
        amount: transferAmount
      });
      setTransferAmount('');
      setMessage('Transfer submitted. Balances refreshed.');
      await onRefresh();
    } catch (error: unknown) {
      setMessage(getErrorMessage(error, 'Unable to transfer between Binance wallets.'));
    } finally {
      setIsTransferring(false);
    }
  };

  const handleConvert = async (event: FormEvent) => {
    event.preventDefault();
    setMessage(null);

    if (!connection?.id || selectedExchange !== 'binance') {
      setMessage('Connect Binance before converting spot assets.');
      return;
    }

    if (!selectedConvertBalance) {
      setMessage('No available spot assets can be converted to USDT.');
      return;
    }

    if (!convertAmount || Number(convertAmount) <= 0) {
      setMessage('Enter a valid convert amount.');
      return;
    }

    if (Number(convertAmount) > selectedConvertBalance.available) {
      setMessage(`Amount exceeds available ${selectedConvertBalance.asset}.`);
      return;
    }

    setIsConverting(true);

    try {
      await binanceExchangeApi.convertSpotAssetToUsdt({
        connectionId: connection.id,
        asset: selectedConvertBalance.asset,
        amount: convertAmount
      });
      setConvertAmount('');
      setMessage(`${selectedConvertBalance.asset} conversion to USDT submitted.`);
      await onRefresh();
    } catch (error: unknown) {
      setMessage(getErrorMessage(error, 'Unable to convert spot asset to USDT.'));
    } finally {
      setIsConverting(false);
    }
  };

  if (!connection) {
    return (
      <div className="border-b border-slate-200 p-4">
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500">
          Connect an exchange account to view wallet balances and transfers.
        </div>
      </div>
    );
  }

  if (selectedExchange !== 'binance') {
    return (
      <div className="border-b border-slate-200 p-4">
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          Wallet balances and transfers are currently live for Binance first.
        </div>
      </div>
    );
  }

  return (
    <div className="border-b border-slate-200 p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-slate-900">Exchange Wallets</div>
          <div className="text-xs text-slate-500">Spot and USD-M Futures balances from Binance.</div>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={isLoading}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:border-blue-200 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <RefreshCw size={13} className={isLoading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => setActiveWallet('spot')}
          className={`rounded-lg border px-3 py-2 text-left transition-colors ${
            activeWallet === 'spot' ? 'border-blue-300 bg-blue-50' : 'border-slate-200 bg-slate-50 hover:bg-slate-100'
          }`}
        >
          <div className="text-xs font-medium text-slate-500">Spot assets</div>
          <div className="mt-1 font-mono text-sm font-semibold text-slate-900">
            {spotBalances.length}
          </div>
          <div className="mt-1 text-[11px] text-slate-500">
            {spotBtcTotal > 0 ? `${formatBalanceAmount(spotBtcTotal)} BTC` : `${spotUsdt ? formatBalanceAmount(spotUsdt.available) : '0'} USDT`}
          </div>
        </button>
        <button
          type="button"
          onClick={() => setActiveWallet('futures')}
          className={`rounded-lg border px-3 py-2 text-left transition-colors ${
            activeWallet === 'futures' ? 'border-blue-300 bg-blue-50' : 'border-slate-200 bg-slate-50 hover:bg-slate-100'
          }`}
        >
          <div className="text-xs font-medium text-slate-500">Futures wallet</div>
          <div className="mt-1 font-mono text-sm font-semibold text-slate-900">
            {futuresUsdt ? formatBalanceAmount(futuresUsdt.available) : formatBalanceAmount(futuresWalletTotal)}
          </div>
          <div className="mt-1 text-[11px] text-slate-500">
            Available USDT
          </div>
        </button>
      </div>

      {uniqueErrors.length > 0 && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          {uniqueErrors.join(' ')}
        </div>
      )}

      <div className="mt-3 max-h-44 space-y-2 overflow-y-auto rounded-lg border border-slate-200 bg-white p-2">
        {isLoading ? (
          <div className="py-4 text-center text-xs text-slate-500">Loading balances...</div>
        ) : visibleBalances.length > 0 ? (
          visibleBalances.map(balance => (
            <div key={`${balance.walletType}-${balance.asset}`} className="rounded bg-slate-50 px-3 py-2">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-semibold text-slate-900">
                  {balance.displayAsset}
                  {balance.balanceKind === 'earn' && (
                    <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">Earn</span>
                  )}
                </span>
                <span className="font-mono text-sm text-slate-900">{formatBalanceAmount(balance.total)}</span>
              </div>
              <div className="mt-1 flex items-center justify-between gap-3 text-[11px] text-slate-500">
                <span>Available {formatBalanceAmount(balance.available)}</span>
                <span>Locked {formatBalanceAmount(balance.locked)}</span>
              </div>
            </div>
          ))
        ) : (
          <div className="py-4 text-center text-xs text-slate-500">No non-zero {activeWallet} balances found.</div>
        )}
      </div>

      <form onSubmit={handleTransfer} className="mt-3 space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
        <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
          <ArrowRightLeft size={14} />
          Transfer Wallet Funds
        </div>
        <div className="text-[11px] leading-relaxed text-slate-500">
          Requires Binance API permission: Permits Universal Transfer.
        </div>
        <select
          value={transferDirection}
          onChange={(event) => setTransferDirection(event.target.value as 'MAIN_UMFUTURE' | 'UMFUTURE_MAIN')}
          className="custom-select w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
        >
          <option value="MAIN_UMFUTURE">Spot to USD-M Futures</option>
          <option value="UMFUTURE_MAIN">USD-M Futures to Spot</option>
        </select>
        <div className="grid grid-cols-[0.9fr_1.1fr] gap-2">
          <select
            value={transferAsset}
            onChange={(event) => setTransferAsset(event.target.value)}
            className="custom-select rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
          >
            {(transferableAssets.length > 0 ? transferableAssets : ['USDT']).map(asset => (
              <option key={asset} value={asset}>{asset}</option>
            ))}
          </select>
          <input
            value={transferAmount}
            onChange={(event) => setTransferAmount(event.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-xs text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            placeholder="0.00"
          />
        </div>
        <button
          type="submit"
          disabled={isTransferring}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-500"
        >
          {isTransferring ? 'Transferring...' : 'Transfer'}
        </button>
        {message && (
          <div className="rounded border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-700">
            {message}
          </div>
        )}
      </form>

      <form onSubmit={handleConvert} className="mt-3 space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
            <ArrowRightLeft size={14} />
            Convert Spot to USDT
          </div>
          {selectedConvertBalance && (
            <button
              type="button"
              onClick={() => setConvertAmount(String(selectedConvertBalance.available))}
              className="rounded bg-white px-2 py-1 text-[11px] font-medium text-blue-600 hover:text-blue-700"
            >
              Max
            </button>
          )}
        </div>
        <div className="grid grid-cols-[0.9fr_1.1fr] gap-2">
          <select
            value={selectedConvertBalance?.asset || ''}
            onChange={(event) => {
              setConvertAsset(event.target.value);
              setConvertAmount('');
            }}
            disabled={convertibleBalances.length === 0}
            className="custom-select rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
          >
            {convertibleBalances.length > 0 ? convertibleBalances.map(balance => (
              <option key={balance.asset} value={balance.asset}>{balance.asset}</option>
            )) : (
              <option value="">No assets</option>
            )}
          </select>
          <input
            value={convertAmount}
            onChange={(event) => setConvertAmount(event.target.value)}
            disabled={!selectedConvertBalance}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-xs text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
            placeholder="0.00"
          />
        </div>
        <div className="text-[11px] text-slate-500">
          {selectedConvertBalance
            ? `Available ${formatBalanceAmount(selectedConvertBalance.available)} ${selectedConvertBalance.asset}`
            : 'Earn balances must be redeemed in Binance before Valore can convert them.'}
        </div>
        <button
          type="submit"
          disabled={isConverting || !selectedConvertBalance}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-500"
        >
          {isConverting ? 'Converting...' : 'Convert to USDT'}
        </button>
      </form>
    </div>
  );
};

const ExchangeTradePanel: React.FC<{
  selectedPair: string;
  selectedExchange: ExchangeId;
  connection?: SavedConnection;
  balances: ExchangeWalletBalance[];
  onBalancesRefresh: () => Promise<void>;
}> = ({ selectedPair, selectedExchange, connection, balances, onBalancesRefresh }) => {
  const { getPriceBySymbol } = useUnifiedPrices();
  const exchange = EXCHANGES.find(item => item.id === selectedExchange) || EXCHANGES[0];
  const [marketType, setMarketType] = useState<MarketType>(exchange.marketTypes[0]);
  const [orderType, setOrderType] = useState<OrderType>('limit');
  const [side, setSide] = useState<OrderSide>('buy');
  const [quantity, setQuantity] = useState('');
  const [limitPrice, setLimitPrice] = useState('');
  const [leverage, setLeverage] = useState('10');
  const [message, setMessage] = useState<string | null>(null);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);

  const currentPrice = useMemo(() => getPriceBySymbol(selectedPair), [getPriceBySymbol, selectedPair]);
  const displaySymbol = selectedExchange === 'coinbase' ? formatPairForCoinbase(selectedPair) : selectedPair;
  const quoteAsset = selectedPair.endsWith('USDT') ? 'USDT' : selectedPair.includes('/') ? selectedPair.split('/')[1] : 'USDT';
  const activeQuoteBalance = balances.find(balance => (
    balance.walletType === marketType && balance.asset === quoteAsset
  ));

  React.useEffect(() => {
    const nextExchange = EXCHANGES.find(item => item.id === selectedExchange) || EXCHANGES[0];
    setMarketType(nextExchange.marketTypes[0]);
  }, [selectedExchange]);

  React.useEffect(() => {
    if (currentPrice > 0 && !limitPrice) {
      setLimitPrice(currentPrice.toFixed(2));
    }
  }, [currentPrice, limitPrice]);

  const notional = useMemo(() => {
    const amount = parseFloat(quantity) || 0;
    const price = orderType === 'market' ? currentPrice : (parseFloat(limitPrice) || currentPrice);
    return amount * price;
  }, [quantity, orderType, currentPrice, limitPrice]);

  const handlePlaceOrder = async () => {
    setMessage(null);

    if (!connection?.id) {
      setMessage('Connect an exchange API key before placing orders.');
      return;
    }

    if (selectedExchange !== 'binance') {
      setMessage(`${exchange.name} execution is planned after Binance is finished.`);
      return;
    }

    if (!quantity || Number(quantity) <= 0) {
      setMessage('Enter a valid quantity before placing an order.');
      return;
    }

    if (orderType === 'limit' && (!limitPrice || Number(limitPrice) <= 0)) {
      setMessage('Enter a valid limit price before placing a limit order.');
      return;
    }

    setIsPlacingOrder(true);

    try {
      const payload = {
        connectionId: connection.id,
        symbol: selectedPair,
        side: side === 'buy' ? 'BUY' as const : 'SELL' as const,
        orderType: orderType === 'market' ? 'MARKET' as const : 'LIMIT' as const,
        quantity,
        price: orderType === 'limit' ? limitPrice : undefined,
      };

      if (marketType === 'spot') {
        await binanceExchangeApi.placeSpotOrder(payload);
      } else {
        await binanceExchangeApi.placeFuturesOrder({
          ...payload,
          leverage,
          marginType: 'isolated'
        });
      }

      setMessage(`${exchange.name} ${marketType} order submitted.`);
      setQuantity('');
      await onBalancesRefresh();
    } catch (error: unknown) {
      setMessage(getErrorMessage(error, 'Unable to place exchange order.'));
    } finally {
      setIsPlacingOrder(false);
    }
  };

  return (
    <div className="flex flex-col bg-white">
      <ExchangeConnectionStatus connection={connection} exchange={exchange.name} />

      <div className="space-y-4 p-4">
        <div className="grid grid-cols-2 gap-2">
          {exchange.marketTypes.map(type => (
            <button
              key={type}
              onClick={() => setMarketType(type)}
              className={`rounded-lg px-3 py-2 text-sm font-medium capitalize transition-colors ${
                marketType === type ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {type}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-2">
          {(['limit', 'market'] as const).map(type => (
            <button
              key={type}
              onClick={() => setOrderType(type)}
              className={`rounded-lg px-3 py-2 text-sm font-medium capitalize transition-colors ${
                orderType === type ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {type}
            </button>
          ))}
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Symbol</label>
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-semibold text-slate-900">
            {displaySymbol}
          </div>
        </div>

        {orderType === 'limit' && (
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Limit price</label>
            <input
              value={limitPrice}
              onChange={(event) => setLimitPrice(event.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 font-mono text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              placeholder="0.00"
            />
          </div>
        )}

        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">Quantity</label>
          <input
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
            className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 font-mono text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            placeholder="0.000000"
          />
        </div>

        {marketType === 'futures' && (
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Leverage</label>
            <input
              value={leverage}
              onChange={(event) => setLeverage(event.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 font-mono text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              placeholder="10"
            />
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setSide('buy')}
            className={`rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
              side === 'buy' ? 'bg-green-500 text-white' : 'bg-green-50 text-green-700 hover:bg-green-100'
            }`}
          >
            Buy / Long
          </button>
          <button
            onClick={() => setSide('sell')}
            className={`rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
              side === 'sell' ? 'bg-red-500 text-white' : 'bg-red-50 text-red-700 hover:bg-red-100'
            }`}
          >
            Sell / Short
          </button>
        </div>

        <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs">
          <div className="flex justify-between">
            <span className="text-slate-500">Exchange balance</span>
            <span className="font-mono text-slate-900">
              {activeQuoteBalance ? `${formatBalanceAmount(activeQuoteBalance.available)} ${quoteAsset}` : `0 ${quoteAsset}`}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Estimated notional</span>
            <span className="font-mono text-slate-900">{notional.toFixed(2)} USDT</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Execution venue</span>
            <span className="font-medium text-slate-900">{exchange.name}</span>
          </div>
        </div>

        <button
          onClick={handlePlaceOrder}
          disabled={isPlacingOrder}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-500"
        >
          <KeyRound size={16} />
          {isPlacingOrder ? 'Submitting Order...' : 'Place Exchange Order'}
        </button>

        {message && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-700">
            {message}
          </div>
        )}
      </div>
    </div>
  );
};

const ExchangeConnectionStatus: React.FC<{ connection?: SavedConnection; exchange: string }> = ({ connection, exchange }) => (
  <div className="border-b border-slate-200 p-4">
    <div className="flex items-center justify-between gap-3">
      <div>
        <div className="text-sm font-semibold text-slate-900">{exchange}</div>
        <div className="mt-1 text-xs text-slate-500">
          {connection ? 'Ready for exchange trading' : 'Connect an API key to use exchange funds.'}
        </div>
      </div>
      <div className={`rounded-full p-2 ${connection ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'}`}>
        {connection ? <Lock size={16} /> : <KeyRound size={16} />}
      </div>
    </div>
  </div>
);

const savedConnectionFromBinance = (connection: BinanceConnection): SavedConnection => ({
  id: connection.id,
  exchange: 'binance',
  label: connection.label,
  maskedKey: 'stored securely',
  connectedAt: connection.last_verified_at || connection.created_at,
  isTestnet: connection.is_testnet
});

const ExchangesTradingPage: React.FC<ExchangesTradingPageProps> = ({
  selectedPair,
  onPairChange
}) => {
  const [selectedExchange, setSelectedExchange] = useState<ExchangeId>('binance');
  const [connections, setConnections] = useState<Partial<Record<ExchangeId, SavedConnection>>>({});
  const [connectionNotice, setConnectionNotice] = useState<string | null>(null);
  const [accountOverview, setAccountOverview] = useState<BinanceAccountOverview | null>(null);
  const [walletBalances, setWalletBalances] = useState<ExchangeWalletBalance[]>([]);
  const [isLoadingBalances, setIsLoadingBalances] = useState(false);

  const connection = connections[selectedExchange];

  const refreshBalances = React.useCallback(async () => {
    if (selectedExchange !== 'binance' || !connection?.id) {
      setAccountOverview(null);
      setWalletBalances([]);
      return;
    }

    setIsLoadingBalances(true);

    try {
      const overview = await binanceExchangeApi.getAccountOverview(connection.id);
      setAccountOverview(overview);
      setWalletBalances(balancesFromOverview(overview));
    } catch (error: unknown) {
      setConnectionNotice(getErrorMessage(error, 'Unable to load Binance balances.'));
      setAccountOverview(null);
      setWalletBalances([]);
    } finally {
      setIsLoadingBalances(false);
    }
  }, [connection?.id, selectedExchange]);

  React.useEffect(() => {
    let isMounted = true;

    const loadBinanceConnection = async () => {
      try {
        const binanceConnections = await binanceExchangeApi.listConnections();
        if (!isMounted || binanceConnections.length === 0) return;

        setConnections(prev => ({
          ...prev,
          binance: savedConnectionFromBinance(binanceConnections[0])
        }));
      } catch (error: unknown) {
        if (isMounted) setConnectionNotice(getErrorMessage(error, 'Unable to load Binance connections.'));
      }
    };

    loadBinanceConnection();

    return () => {
      isMounted = false;
    };
  }, []);

  React.useEffect(() => {
    refreshBalances();
  }, [refreshBalances]);

  const handleConnect = async (params: {
    exchange: ExchangeId;
    label: string;
    apiKey: string;
    apiSecret: string;
    passphrase: string;
    coinbaseKeyName: string;
    coinbasePrivateKey: string;
    isTestnet: boolean;
  }) => {
    if (params.exchange === 'binance') {
      const binanceConnection = await binanceExchangeApi.connect({
        label: params.label,
        apiKey: params.apiKey,
        apiSecret: params.apiSecret,
        isTestnet: params.isTestnet
      });

      setConnections(prev => ({
        ...prev,
        binance: savedConnectionFromBinance(binanceConnection)
      }));
      setConnectionNotice('Binance connection verified and saved.');
      setAccountOverview(null);
      setWalletBalances([]);
      return;
    }

    setConnections(prev => ({
      ...prev,
      [params.exchange]: {
        exchange: params.exchange,
        label: params.label,
        maskedKey: maskApiKey(params.exchange === 'coinbase' ? params.coinbaseKeyName : params.apiKey),
        connectedAt: new Date().toISOString(),
        isTestnet: params.isTestnet
      }
    }));
    setConnectionNotice(`${EXCHANGES.find(item => item.id === params.exchange)?.name || 'Exchange'} UI saved locally. Backend connector is still pending.`);
  };

  const handleDisconnect = async () => {
    const currentConnection = connections[selectedExchange];

    if (selectedExchange === 'binance' && currentConnection?.id) {
      await binanceExchangeApi.disconnect(currentConnection.id);
    }

    setConnections(prev => {
      const next = { ...prev };
      delete next[selectedExchange];
      return next;
    });
    setAccountOverview(null);
    setWalletBalances([]);
    setConnectionNotice('Exchange connection removed.');
  };

  const emptyAction = async () => true;
  const emptyClose = () => undefined;

  const terminal = (
    <TradingTerminalLayout
      header={
        <TradingPairHeader
          selectedPair={selectedPair}
          tradingMode="exchanges"
          onPairChange={onPairChange}
        />
      }
      leftPanel={
        <OrderBookWithTrades
          selectedPair={selectedPair}
          tradingMode="exchanges"
        />
      }
      chartToolbar={<ChartToolbar />}
      chart={<TradingChart key={selectedPair} selectedPair={selectedPair} />}
      rightPanel={
        <div className="h-full max-h-full min-h-0 overflow-y-scroll overscroll-contain bg-white pb-6">
          <ExchangeConnectionPanel
            selectedExchange={selectedExchange}
            onExchangeChange={setSelectedExchange}
            connection={connection}
            onConnect={handleConnect}
            onDisconnect={handleDisconnect}
          />
          <ExchangeAccountPanel
            selectedExchange={selectedExchange}
            connection={connection}
            balances={walletBalances}
            overview={accountOverview}
            isLoading={isLoadingBalances}
            onRefresh={refreshBalances}
          />
          <ExchangeTradePanel
            selectedPair={selectedPair}
            selectedExchange={selectedExchange}
            connection={connection}
            balances={walletBalances}
            onBalancesRefresh={refreshBalances}
          />
          {connectionNotice && (
            <div className="border-t border-slate-200 bg-blue-50 px-4 py-3 text-xs text-blue-700">
              {connectionNotice}
            </div>
          )}
        </div>
      }
      bottomPanel={
        <PositionsPanel
          positions={[]}
          openOrders={[]}
          positionHistory={[]}
          orderHistory={[]}
          onClosePosition={emptyClose}
          onCancelOrder={emptyAction}
          onCancelAllOrders={emptyAction}
        />
      }
    />
  );

  return (
    <>
      <div className="hidden lg:block h-full">{terminal}</div>
      <div className="lg:hidden min-h-screen bg-white text-slate-900">
        <TradingPairHeader
          selectedPair={selectedPair}
          tradingMode="exchanges"
          onPairChange={onPairChange}
        />
        <div className="h-[420px] border-b border-slate-200">
          <TradingChart key={selectedPair} selectedPair={selectedPair} />
        </div>
        <ExchangeConnectionPanel
          selectedExchange={selectedExchange}
          onExchangeChange={setSelectedExchange}
          connection={connection}
          onConnect={handleConnect}
          onDisconnect={handleDisconnect}
        />
        <ExchangeAccountPanel
          selectedExchange={selectedExchange}
          connection={connection}
          balances={walletBalances}
          overview={accountOverview}
          isLoading={isLoadingBalances}
          onRefresh={refreshBalances}
        />
        <ExchangeTradePanel
          selectedPair={selectedPair}
          selectedExchange={selectedExchange}
          connection={connection}
          balances={walletBalances}
          onBalancesRefresh={refreshBalances}
        />
        {connectionNotice && (
          <div className="border-t border-slate-200 bg-blue-50 px-4 py-3 text-xs text-blue-700">
            {connectionNotice}
          </div>
        )}
        <div className="h-[320px] border-t border-slate-200">
          <PositionsPanel
            positions={[]}
            openOrders={[]}
            positionHistory={[]}
            orderHistory={[]}
            onClosePosition={emptyClose}
            onCancelOrder={emptyAction}
            onCancelAllOrders={emptyAction}
          />
        </div>
      </div>
    </>
  );
};

export default ExchangesTradingPage;
